import { UserCustomSignatureInput } from "@/domain/entities/user/UserCustomSignature";
import { queryKeys } from "@/lib/queryKeys";
import { userSignatureToDomainMapper } from "@/mappers/user/userSignatureMapper";
import { useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import {
    useMutation,
    UseMutationResult,
    useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useErrorToast from "../utils/useErrorToast";
import { useToast } from "../utils/useToast";

const useCreateUserSignature = (): {
    createUserSignature: UseMutationResult<
        void,
        Error,
        UserCustomSignatureInput
    >;
} => {
    const { userManager } = useSdk();
    const queryClient = useQueryClient();
    const showErrorToast = useErrorToast();
    const { toast } = useToast();
    const { t } = useTranslation();

    const createUserSignatureFn = async ({
        avatar,
        name,
        email,
        phone,
        jobTitle,
        companyName,
        website,
        other,
    }: UserCustomSignatureInput): Promise<void> => {
        const signatureParams = new UserCustomSignatureInput({
            avatar,
            name,
            jobTitle,
            email,
            companyName,
            phone,
            website,
            other,
        });

        try {
            const result =
                await userManager.createUserSignature(signatureParams);

            const resultSignatureParams = userSignatureToDomainMapper(result);

            queryClient.setQueryData(
                queryKeys.user.signature(),
                resultSignatureParams,
            );

            toast({
                description: t("settings.signature.form.saveSuccess"),
                variant: "success",
            });
        } catch (error) {
            console.error(error);
            showErrorToast({
                titleTranslationKey: "errors.signature.create",
            });
        }
    };

    const createUserSignature = useMutation({
        mutationFn: (signatureParams: UserCustomSignatureInput) =>
            createUserSignatureFn(signatureParams),
    });

    return {
        createUserSignature,
    };
};

export default useCreateUserSignature;
